/**
 * StatusFilter plugin - multi-select filter for call statuses.
 *
 * URL param: status (same singular/plural)
 * API param: status (same as URL)
 * SavedView field: statuses
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4)
 */

import React, { useMemo } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters } from "../../../../../services/grantsApi";
import MultiSelectDropdown from "../../../../common/MultiSelectDropdown";
import { STATUS_LABELS } from "../../../../../constants/grants.constants";
import { StatusCount } from "../../../../../types/grants";

/**
 * Status filter React component.
 * Renders a multi-select dropdown for status filtering.
 */
const StatusFilterComponent = React.memo(function StatusFilterComponent(
  props: FilterComponentProps<string[]>
) {
  const { value, onChange, facets, isLoading } = props;

  const options = useMemo(
    () =>
      ((facets?.statusCounts as StatusCount[]) ?? []).map((item) => ({
        value: item.status,
        label: STATUS_LABELS[item.status] || item.status,
        count: item.count,
      })),
    [facets?.statusCounts]
  );

  if (isLoading) {
    return React.createElement("div", {
      className: "w-64 h-10 bg-neutral-200 rounded-md animate-pulse",
    });
  }

  return React.createElement(
    "div",
    { className: "w-64 relative" },
    React.createElement(MultiSelectDropdown, {
      options,
      selectedValues: value,
      onChange,
      placeholder: "Wszystkie statusy",
      ariaLabel: "Filtruj według statusu",
    })
  );
});

export const statusFilter: FilterPlugin<string[]> = {
  key: "status",
  label: "Status",
  placement: "main",
  order: 3,
  defaultValue: [],
  Component: StatusFilterComponent,

  isActive(value: string[]): boolean {
    return value.length > 0;
  },

  toUrlParams(value: string[], params: URLSearchParams): void {
    value.forEach((s) => params.append("status", s));
  },

  fromUrlParams(params: URLSearchParams): string[] {
    return params.getAll("status");
  },

  toSavedView(value: string[], filters: Partial<SavedViewFilters>): void {
    filters.statuses = value;
  },

  fromSavedView(filters: SavedViewFilters): string[] {
    return filters.statuses ?? [];
  },

  toApiParams(value: string[], params: URLSearchParams): void {
    value.forEach((s) => params.append("status", s));
  },
};
